import { FC, useContext, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { useI18n } from "../../hooks/useTranslations";
import { useCarPlanningsApi } from "../../http/carPlannings/api";
import Button from "../ui/Button";
import FileUploader from "../ui/FileUploader";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import ModalDialog from "../ui/ModalDialog";

interface CarPlanningImportFormBody {
  file: File;
}

const CarPlanningImportButton: FC = () => {
  const i18n = useI18n();
  const [show, setShow] = useState(false);
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const carPlanningApi = useCarPlanningsApi();

  const onSubmit = async (formData: CarPlanningImportFormBody) => {
    if (!formData.file) {
      return dispatch(showErrorNotification(i18n, null, i18n.t.common.noFileSelected));
    }

    const form = new FormData();
    form.append("file", formData.file); // input file

    setIsSubmitting(true);
    try {
      await carPlanningApi.uploadCsv(form);
      dispatch(showSuccessNotification(i18n.t));
      setShow(false);
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button buttonProps={{ onClick: () => setShow(true) }}>{i18n.t.common.import}</Button>
      {!!show && (
        <ModalDialog onClose={() => setShow(false)} title={i18n.t.common.import}>
          <Form<CarPlanningImportFormBody>
            submitText={i18n.t.common.save}
            disabled={isSubmitting}
            onSubmit={() => (formData) => onSubmit(formData)}
          >
            {({ control, setValue }) => {
              return (
                <FormFieldsContainer>
                  <Controller
                    name="file"
                    control={control}
                    rules={{ required: true }}
                    render={({ fieldState }) => (
                      <FormField label={i18n.t.common.file} error={fieldState.error}>
                        <FileUploader
                          isSubmitting={isSubmitting}
                          disabled={isSubmitting}
                          onChange={(f) => setValue("file", f)}
                          accept=".xlsx,.csv"
                        />
                      </FormField>
                    )}
                  />
                </FormFieldsContainer>
              );
            }}
          </Form>
        </ModalDialog>
      )}
    </>
  );
};

export default CarPlanningImportButton;
